import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { forgotPassword } from '../../../api/auth';
import useForm, { ErrorMessagesCode } from '../../../hooks/useForm';
import { useNavigate } from '../../../hooks/useNavigate';
import { MButton } from '../../../horizon-components/MButton/ui/MButton';
import { MInput } from '../../../horizon-components/MInput/MInput';
import {
  AuthRoutes,
  MainRoutes,
} from '../../../horizon-layout/MainLayout/Routes/types/routes';
import getErrorMessages from '../../../utils/getErrorMessages';
import { AuthLayout } from '../../../widgets/AuthLayout';

type SignInForm = {
  email: string;
};

const ResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation(['translation']);

  const { values, onChange, errors, validate } = useForm<SignInForm>({
    email: '',
  });

  const schema = useMemo(
    () =>
      yup.object().shape({
        email: yup
          .string()
          .email(t(ErrorMessagesCode.email))
          .required(t(ErrorMessagesCode.required)),
      }),
    []
  );

  const resetPasswordHandler = async () => {
    setError('');
    const hasErrors = await validate(schema);

    if (hasErrors) {
      return;
    }

    setIsLoading(true);
    forgotPassword(values.email)
      .then(() => {
        navigate(MainRoutes.auth + AuthRoutes.code, {
          state: { email: values.email },
        });
      })
      .catch((error) => {
        error?.response?.data &&
          setError(
            getErrorMessages(error?.response?.data) || t('profile.forgotErrorEmail')
          );
        return error;
      })
      .finally(() => setIsLoading(false));
  };

  const handleEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.key === 'Enter' && resetPasswordHandler();
  };

  return (
    <AuthLayout title={t('profile.forgot')} desc={t('profile.forgotInfo')}>
      <div className="flex flex-col gap-4">
        <MInput
          label={t('profile.email')}
          placeholder="example@mail.com"
          id="email"
          name={'email'}
          type="text"
          bottomText={errors.email}
          state={errors.email ? 'error' : 'default'}
          value={values.email}
          onChange={onChange}
          inputClassName={'w-full leading-[18px]'}
          wrapperClassName={'w-full'}
          onKeyUp={handleEnter}
        />
        {!!error && (
          <p className={'text-center text-xs text-red-600 md:text-sm'}>{error}</p>
        )}
        <MButton
          variant="highlighted"
          color="primary"
          onClick={resetPasswordHandler}
          disabled={isLoading}
          size={'md'}
          loading={isLoading}
        >
          {t('profile.continue')}
        </MButton>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
